/**
 * Durable dictation-vs-full-recording classification (GDN-2510).
 *
 * The recording screen and note popover must decide "is this encounter
 * dictation-only?" from evidence that SURVIVES session cleanup, encounter
 * refetches, and page reloads. Live recorder state dies at cleanUpSession(),
 * and the encounter's `inputs[]` reference titles are unreliable: the backend
 * appends recording refs WITHOUT a title (document_service builds the ref from
 * id/type/created_at only), so any refetched encounter carries
 * `title: null` refs even though the persisted DOCUMENT is titled
 * "Encounter Recording". Classifying from ref titles alone therefore flips a
 * full recording to "dictation" the moment the optimistic cache entry (which
 * does carry the title) is replaced by server state — the founder-reported
 * misclassification bug.
 *
 * Durable evidence, in order of trust per recording:
 *   1. Title — the ref's when present (optimistic path), else the fetched
 *      document's. Titles are assigned at save time from the true elapsed
 *      seconds ("Encounter Recording" vs "Dictation N") and reflect what the
 *      save path actually did to the encounter clock.
 *   2. Elapsed seconds — the fetched document's `description` field (the save
 *      paths store the elapsed seconds there), classified via the shared
 *      threshold.
 *
 * A recording with neither signal is 'unknown'; an encounter with any unknown
 * (and no confirmed full) recording classifies as 'unknown', which callers
 * MUST treat as NOT dictation-only: presenting a full recording as a dictation
 * (amber ask state, "Time of dictation" labeling, scheduled auto-default) is
 * the harmful direction, while presenting a dictation factually is benign.
 */

import { parseUTCDate } from '@utils/dateUtils';

import { isFullEncounterRecording } from './dictationThreshold';

/** The signals a single recording classifies from. */
export interface RecordingSignals {
  /** Best-known title: input ref's when present, else the fetched document's. */
  title?: string | null;
  /** Elapsed seconds from the recording document's `description`, when known. */
  elapsedSeconds?: number | null;
}

export type RecordingKind = 'full' | 'dictation' | 'unknown';

export type EncounterRecordingClass =
  | 'none'
  | 'full'
  | 'dictation_only'
  | 'unknown';

const FULL_RECORDING_TITLE_PREFIX = 'Encounter Recording';
const DICTATION_TITLE_PREFIX = 'Dictation';

/** Parse a recording document's `description` (elapsed seconds) — null when absent/invalid. */
export const parseRecordingElapsedSeconds = (
  description: string | null | undefined,
): number | null => {
  if (!description) return null;
  const parsed = parseInt(description, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** Classify one recording from its durable signals. Title wins over elapsed. */
export const classifyRecording = ({ title, elapsedSeconds }: RecordingSignals): RecordingKind => {
  if (title?.startsWith(FULL_RECORDING_TITLE_PREFIX)) return 'full';
  if (title?.startsWith(DICTATION_TITLE_PREFIX)) return 'dictation';
  if (elapsedSeconds != null) {
    return isFullEncounterRecording(elapsedSeconds) ? 'full' : 'dictation';
  }
  return 'unknown';
};

/**
 * Classify the encounter's recordings as a whole.
 *
 * Mixed-media rule: ANY full recording → 'full' (the full recording owns the
 * encounter clock regardless of additional dictations). Any unclassifiable
 * recording (short of a confirmed full) → 'unknown', never 'dictation_only'.
 */
export const classifyEncounterRecordings = (
  recordings: RecordingSignals[],
): EncounterRecordingClass => {
  if (recordings.length === 0) return 'none';
  const kinds = recordings.map(classifyRecording);
  if (kinds.includes('full')) return 'full';
  if (kinds.includes('unknown')) return 'unknown';
  return 'dictation_only';
};

/**
 * Guard input for the dictation scheduled auto-default: block whenever a full
 * recording exists OR might exist ('unknown'). Never auto-default over a
 * clock that could belong to a full recording.
 */
export const hasPossibleFullRecording = (
  classification: EncounterRecordingClass,
): boolean => classification === 'full' || classification === 'unknown';

// ---------------------------------------------------------------------------
// Ref + fetched-document merging (pure; the useRecordingInputs hook feeds it).
// ---------------------------------------------------------------------------

/** The `encounter.inputs[]` reference shape the helpers need. */
export interface RecordingRefLike {
  encounter_document_id: string;
  type: string;
  title?: string | null;
  created_at?: string | null;
}

/** The fetched recording-document shape the helpers need. */
export interface RecordingDocLike {
  title?: string | null;
  /** Elapsed seconds, stored as a stringified integer by the save paths. */
  description?: string | null;
  created_at?: string | null;
}

export const isRecordingRef = (ref: RecordingRefLike): boolean =>
  ref.type === 'recording';

/** Optimistic (not yet persisted) refs — no document exists to fetch. */
export const isTempRecordingRef = (ref: RecordingRefLike): boolean =>
  ref.encounter_document_id.startsWith('temp-');

/**
 * Merge each recording ref with its fetched document into classification
 * signals. Ref title wins when present (the optimistic path carries it); the
 * fetched document supplies title/elapsed for server-refetched refs, whose
 * titles come back null.
 */
export const buildRecordingSignals = (
  refs: RecordingRefLike[],
  docsById: Record<string, RecordingDocLike | undefined>,
): RecordingSignals[] =>
  refs.filter(isRecordingRef).map(ref => {
    const doc = docsById[ref.encounter_document_id];
    return {
      title: ref.title ?? doc?.title ?? null,
      elapsedSeconds: parseRecordingElapsedSeconds(doc?.description),
    };
  });

/**
 * The recording window recoverable from the persisted documents. "In room"
 * means the actual session, so we pick the LONGEST recording — a full
 * encounter recording, not a short dictation added afterward (GDN-2510).
 *
 * `created_at` is (approximately) when a recording stopped, so for the chosen
 * recording end = created_at and start = created_at − elapsed seconds. Among
 * recordings with a known duration the longest wins (tiebreak: earliest — the
 * session proper). When no recording carries a duration, the earliest
 * timestamp stands as a start-only window. Null when nothing is usable.
 */
export const deriveRecordingWindowFromDocs = (
  refs: RecordingRefLike[],
  docsById: Record<string, RecordingDocLike | undefined>,
): { start: Date; end: Date | null } | null => {
  // created_at comes from Mongo-backed documents whose datetimes serialize
  // WITHOUT a timezone suffix, but they denote UTC instants — parse
  // suffix-aware (naive → UTC) and order by instant, never by bare `new Date`
  // (which reads naive strings as local) or string comparison (which
  // misorders mixed naive/suffixed values).
  const dated = refs
    .filter(ref => isRecordingRef(ref) && !isTempRecordingRef(ref))
    .map(ref => {
      const doc = docsById[ref.encounter_document_id];
      const createdAt = doc?.created_at ?? ref.created_at ?? null;
      const createdAtDate = createdAt ? parseUTCDate(createdAt) : null;
      return {
        createdAtDate,
        elapsedSeconds: parseRecordingElapsedSeconds(doc?.description),
      };
    })
    .filter(
      (entry): entry is { createdAtDate: Date; elapsedSeconds: number | null } =>
        entry.createdAtDate != null && !Number.isNaN(entry.createdAtDate.getTime()),
    );
  if (dated.length === 0) return null;

  // Prefer the longest recording (the in-room session) over a shorter dictation
  // added afterward. Among recordings with a known duration, longest wins;
  // tiebreak on the earliest, which is the session proper.
  const withElapsed = dated.filter(
    (e): e is { createdAtDate: Date; elapsedSeconds: number } => e.elapsedSeconds != null,
  );
  if (withElapsed.length > 0) {
    const best = withElapsed.reduce((a, b) => {
      if (b.elapsedSeconds !== a.elapsedSeconds) {
        return b.elapsedSeconds > a.elapsedSeconds ? b : a;
      }
      return b.createdAtDate.getTime() < a.createdAtDate.getTime() ? b : a;
    });
    const end = best.createdAtDate;
    return { start: new Date(end.getTime() - best.elapsedSeconds * 1000), end };
  }

  // No durations known — the earliest timestamp is the best guess, start-only.
  const earliest = dated.reduce((a, b) =>
    b.createdAtDate.getTime() < a.createdAtDate.getTime() ? b : a,
  );
  return { start: earliest.createdAtDate, end: null };
};

/**
 * Combine two candidate recording windows, preferring the LONGER (the full
 * session over a short dictation). A window with a known duration always beats
 * one with an unknown end; nulls fall through to the other side. Used to merge
 * the live-recorder window with the document-derived window so a trailing
 * dictation can never shrink the displayed "in room" time (GDN-2510).
 */
export const longerRecordingWindow = <T extends { start: Date; end: Date | null }>(
  a: T | null,
  b: T | null,
): T | null => {
  if (!a) return b;
  if (!b) return a;
  const durationMs = (w: { start: Date; end: Date | null }) =>
    w.end ? w.end.getTime() - w.start.getTime() : -1;
  return durationMs(b) > durationMs(a) ? b : a;
};
